import React, { useState, useEffect } from 'react';
import { useToggle } from 'components/shared/hooks';
import { TEXT_VARIANTS, Text } from 'components/ui/Typography';
import { Spinner } from 'components/ui';
import { routes } from 'constants/config';
import request from 'utils/request_ajax';
import useAutosize from 'components/shared/hooks/useAutosize';
import InfoTooltip from 'components/shared/InfoTooltip';
import AIAlert from 'components/shared/AI/AlAlert';
import Error from 'components/InPersonTraining/shared/Error';
import EditControls from './EditControls';
import ConfirmationModal from './ConfirmationModal';

const Summary = ({
  summary,
  inPublishedState,
  isArchived,
  updateHandler,
  lessonId,
}) => {
  const [editMode, toggleEditMode] = useToggle(false);
  const [modalVisible, toggleModalVisible] = useToggle(false);
  const [loading, setLoading] = useState(false);
  const [isGenerated, setIsGenerated] = useState(false);
  const [error, setError] = useState(false);
  const [summaryText, setSummaryText] = useState('');
  const autosizeRef = useAutosize();

  const DEFAULT_TEXT = t('lessons.recap.summary.default_text');

  useEffect(() => {
    setSummaryText(summary);
  }, [summary]);

  const onCancelClick = () => {
    setError(false);
    setLoading(false);
    setIsGenerated(false);
    setSummaryText(summary);
    toggleEditMode(false);
  };

  const getGPTResponse = () => {
    setError(false);
    setLoading(true);
    setIsGenerated(false);
    request
      .get(routes.LESSON_OVERVIEW.GENERATE_SUMMARY(lessonId))
      .then(res => {
        const { recap: generatedSummary } = res;
        setSummaryText(generatedSummary);
        setLoading(false);
        setIsGenerated(true);
      })
      .catch(e => {
        setLoading(false);
        setIsGenerated(false);
        setError(true);
        console.error(e);
      });
  };

  const discardAndGenerate = () => {
    setSummaryText('');
    toggleModalVisible(false);
    getGPTResponse();
  };

  const generateSummary = () => {
    if (summaryText) {
      toggleModalVisible(true);
    } else {
      getGPTResponse();
    }
  };

  const updateSummary = () => {
    updateHandler('recap', summaryText);
    setIsGenerated(false);
    toggleEditMode(false);
    setError(false);
  };

  const preventLineBreaks = e => {
    if (e.key === 'Enter') e.preventDefault();
  };

  const handlePaste = e => {
    e.preventDefault();
    const cleanedInputVal = e.clipboardData
      .getData('text')
      .replace(/[\r\n|\n|\s]+/g, ' ')
      .trim();

    e.target.value = cleanedInputVal;
    setSummaryText(cleanedInputVal);
    global.autosize.update(e.target);
  };

  return (
    <>
      <section className="Summary Module EditableModule f-1">
        <div className="Module-heading">
          <EditControls
            editMode={editMode}
            toggleEditMode={toggleEditMode}
            onCancelClick={onCancelClick}
            updateHandler={updateSummary}
            generateGPTResponse={generateSummary}
            readOnly={loading}
            inPublishedState={inPublishedState}
            isArchived={isArchived}
          />
          <Text component="h3" variant={TEXT_VARIANTS.BODY} className="mb-0">
            {t('lessons.recap.summary.title')}
            <InfoTooltip
              className="ml-8"
              placement="right"
              tooltip={t('lessons.recap.summary.tooltip')}
            />
          </Text>
        </div>
        {editMode && loading && (
          <div className="Summary-loading EditableModule-well">
            <Spinner />
          </div>
        )}
        {editMode && !loading && (
          <textarea
            id="summaryTextArea"
            name="summaryTextArea"
            className="EditableModule-well"
            aria-label={t('lessons.recap.summary.title')}
            ref={autosizeRef}
            onChange={e => setSummaryText(e.target.value)}
            onKeyDown={preventLineBreaks}
            onPaste={handlePaste}
            value={summaryText}
          />
        )}
        {isGenerated && (
          <AIAlert tooltipText={t('actions.ai.lesson_recap_tooltip')} />
        )}
        {!editMode && (
          <p className="text-wrap">{summaryText || DEFAULT_TEXT}</p>
        )}
        {error && (
          <Error
            message={t('lessons.recap.summary.error_generating_summary')}
          />
        )}
      </section>
      {modalVisible && (
        <ConfirmationModal
          closeModal={() => toggleModalVisible(false)}
          discardAndGenerate={discardAndGenerate}
          feature="summary"
        />
      )}
    </>
  );
};

export default Summary;
